import { useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Download, ExternalLink, X, Eye } from 'lucide-react';

/**
 * Gmail-style attachment row for the generated procurement PDFs (Indent / PO /
 * GRN / Issue Note). Each doc renders as a card chip:
 *   - click the card  → preview the document inside a modal (iframe loads the
 *     print page with ?preview=1 so it doesn't auto-open the print dialog)
 *   - Download button → opens the print page in a new tab; that page auto-runs
 *     window.print() so the user lands on the browser "Save as PDF" sheet.
 *
 * The print pages are HTML (browser Save-as-PDF), not server-rendered PDFs, so
 * "preview" = the rendered print page in an iframe and "download" = the
 * print-to-PDF flow. Same files the old download buttons pointed at.
 */

export interface DocAttachment {
  /** Stable key. */
  key: string;
  /** Short type label shown on the chip, e.g. "Indent", "PO", "GRN". */
  kind: string;
  /** Human label, e.g. "Indent IN24". */
  label: string;
  /** Base print URL (without the preview flag), e.g. "/pos/123/print?client=…". */
  url: string;
}

function withPreview(url: string): string {
  return url + (url.includes('?') ? '&' : '?') + 'preview=1';
}

/** Tailwind accent per document kind so the chips are visually scannable. */
function accent(kind: string): { bg: string; text: string; ring: string } {
  const k = kind.toLowerCase();
  if (k.includes('indent')) return { bg: 'bg-indigo-50', text: 'text-indigo-600', ring: 'hover:border-indigo-300' };
  if (k.includes('po')) return { bg: 'bg-emerald-50', text: 'text-emerald-600', ring: 'hover:border-emerald-300' };
  if (k.includes('grn')) return { bg: 'bg-amber-50', text: 'text-amber-600', ring: 'hover:border-amber-300' };
  if (k.includes('issue')) return { bg: 'bg-rose-50', text: 'text-rose-600', ring: 'hover:border-rose-300' };
  return { bg: 'bg-slate-50', text: 'text-slate-600', ring: 'hover:border-slate-300' };
}

export function DocAttachments({ docs }: { docs: DocAttachment[] }) {
  const [preview, setPreview] = useState<DocAttachment | null>(null);
  if (!docs.length) return null;

  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">
        Documents · {docs.length} attachment{docs.length === 1 ? '' : 's'}
      </div>
      <div className="flex flex-wrap gap-3">
        {docs.map((d) => {
          const a = accent(d.kind);
          return (
            <div
              key={d.key}
              className={`group relative w-56 rounded-lg border border-slate-200 bg-white overflow-hidden transition ${a.ring} hover:shadow-md`}
            >
              {/* Preview surface — click to open the modal */}
              <button
                type="button"
                onClick={() => setPreview(d)}
                className={`w-full ${a.bg} h-20 flex items-center justify-center relative`}
                title={`Preview ${d.label}`}
              >
                <FileText className={a.text} size={34} strokeWidth={1.5} />
                <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/40 text-white text-xs font-medium gap-1">
                  <Eye size={14} /> Preview
                </span>
              </button>
              {/* Filename row */}
              <div className="flex items-center gap-2 px-2.5 py-2 border-t border-slate-100">
                <FileText className={`${a.text} shrink-0`} size={16} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-slate-700 truncate" title={d.label}>{d.label}</div>
                  <div className="text-[10px] uppercase text-slate-400 tracking-wide">PDF</div>
                </div>
                <a
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-brand-600 hover:bg-slate-100"
                  title={`Download ${d.label}`}
                >
                  <Download size={15} />
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {preview && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 80 }}
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-5xl h-[92dvh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className={accent(preview.kind).text} size={18} />
                <span className="font-semibold text-slate-800 truncate">{preview.label}</span>
                <span className="text-[10px] uppercase text-slate-400 tracking-wide border border-slate-200 rounded px-1.5 py-0.5">PDF</span>
              </div>
              <div className="flex items-center gap-1.5">
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-brand-600 text-white hover:bg-brand-700"
                >
                  <Download size={14} /> Download PDF
                </a>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
                  title="Open in new tab"
                >
                  <ExternalLink size={14} />
                </a>
                <button
                  onClick={() => setPreview(null)}
                  className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <iframe
              key={preview.key}
              src={withPreview(preview.url)}
              title={preview.label}
              className="flex-1 w-full bg-slate-100"
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
